import React, { PureComponent } from 'react';
import connect from 'react-redux/es/connect/connect';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import FormControl from '@material-ui/core/FormControl/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel';
import Radio from '@material-ui/core/Radio/Radio';
import DoneIcon from '@material-ui/icons/DoneOutline';
import CancelIcon from '@material-ui/icons/CancelOutlined';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid/Grid';

import DataTable from 'Components/DataTable/DataTable';
import Button from 'Common/CustomButtons/Button';
import ItemGrid from 'Common/Grid/ItemGrid';
import FormGroup from 'Components/FormGroup';
import EmailDialog from './EmailDialog';
import { load, loadAutocomplete, updateFilter, loadRoles } from 'Actions/user';
import { loadAutocomplete as loadInterestsAutocomplete } from 'Actions/interest';
import { assignReviewers } from 'Actions/submission';
import { formatDate } from 'Utils';
import { DataTableMetadataType, QueryFilterType, RoleType, UserType } from 'Types';
import {
    ASSIGN_STATUS_ACCEPTED, ASSIGN_STATUS_COMPLETED,
    DATA_TABLE_COLUMN_TYPE_ICON_STATUS,
    DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_MULTI,
    DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE, ROLE_ADMIN,
    ROLE_EDITORIAL_BOARD, ROLE_REVIEWER,
} from 'Constants';
import { aclDecorator, lightboxDecorator } from 'Decorators';

@lightboxDecorator({
    title: 'title.warning',
    text: 'confirm.skip.reviewers',
})
@connect(
    store => ({
        data: store.userList.data,
        filter: store.userList.filter,
        metadata: store.userList.metadata,
        authUser: store.auth.user,
        dataAutocomplete: store.userListAutocomplete.data,
        filterAutocomplete: store.userListAutocomplete.filter,
        dataInterestsAutocomplete: store.interestsAutocomplete.data,
        filterInterestsAutocomplete: store.interestsAutocomplete.filter,
        rolesData: store.userListRoles.data,
        hasFullPool: store.auth.user.has_full_pool,
    }),
    dispatch => ({
        load: filter => dispatch(load(filter)),
        updateFilter: filter => dispatch(updateFilter(filter)),
        loadAutocomplete: filter => dispatch(loadAutocomplete(filter)),
        loadInterestsAutocomplete: filter => dispatch(loadInterestsAutocomplete(filter)),
        loadRoles: () => dispatch(loadRoles()),
        assignReviewers: data => dispatch(assignReviewers(data)),
    }),
)
@aclDecorator()
export default class ReviewersAssignmentList extends PureComponent {
    /**
     * PropTypes
     */
    static propTypes = {
        data: PropTypes.arrayOf(UserType),
        filter: QueryFilterType,
        metadata: DataTableMetadataType,
        load: PropTypes.func.isRequired,
        authUser: PropTypes.object.isRequired,
        updateFilter: PropTypes.func.isRequired,
        loadRoles: PropTypes.func,
        assignReviewers: PropTypes.func.isRequired,
        rolesData: PropTypes.arrayOf(RoleType),
        journalId: PropTypes.number.isRequired,
        submissionId: PropTypes.number.isRequired,
        hasFullPool: PropTypes.number,
        toggleAssignReviewers: PropTypes.func.isRequired,
        openLightbox: PropTypes.func.isRequired,
        is: PropTypes.func.isRequired,
        template: PropTypes.object.isRequired,
        templateHandle: PropTypes.object.isRequired,
        //filter by email
        dataAutocomplete: PropTypes.arrayOf(UserType),
        filterAutocomplete: QueryFilterType,
        loadAutocomplete: PropTypes.func,
        //filter by interests
        dataInterestsAutocomplete: PropTypes.arrayOf(UserType),
        filterInterestsAutocomplete: QueryFilterType,
        loadInterestsAutocomplete: PropTypes.func,
        profiles: PropTypes.array,
    };

    state = {
        isBoardMembersOnly: 'false',
        checkedReviewers: [],
    };

    /**
     * Component Did Mount
     */
    componentDidMount() {
        const { loadRoles, updateFilter } = this.props;
        loadRoles();
        updateFilter({ where: {}, has: {} });
    }

    getFilter = () => {
        const { filter, rolesData, journalId, hasFullPool, is, profiles } = this.props;
        const { isBoardMembersOnly } = this.state;

        const roles = isBoardMembersOnly === 'true' ?
            { role_id: rolesData.find(role => role.slug === ROLE_EDITORIAL_BOARD).id, journal_id: journalId } :
            {
                role_id: {
                    inq: rolesData.filter(role => [ROLE_EDITORIAL_BOARD, ROLE_REVIEWER].includes(role.slug))
                        .map(role => role.id),
                },
                ...(hasFullPool || is(ROLE_ADMIN) ? {} : { journal_id: journalId }),
            };

        return filter.merge({
            include: ['interests', 'assignments', 'userRoles.role'],
            has: filter.has.merge({ userRoles: { where: roles } }),
            where: filter.where.merge({ email: { nin: profiles.map(profile => profile.email) } }),
        });
    };

    loadOptions = input => {
        const { filterAutocomplete, loadAutocomplete, profiles } = this.props;
        const filter = this.getFilter();
        loadAutocomplete(filterAutocomplete.merge(filter.merge({
            where: {
                email: {
                    like: `%${input}%`,
                    nin: profiles.map(profile => profile.email),
                },
            },
        })));
    };

    loadInterestsOptions = input => {
        const { filterInterestsAutocomplete, loadInterestsAutocomplete } = this.props;
        loadInterestsAutocomplete(filterInterestsAutocomplete.merge({ where: { title: { like: `%${input}%` } } }));
    };

    handleChange = reviewer => event => {
        const { checkedReviewers } = this.state;
        this.setState({
            checkedReviewers: event.target.checked ?
                checkedReviewers.concat(reviewer) :
                checkedReviewers.filter(checked => checked !== reviewer),
        });
    };

    handleAssignReviewers = (email) => {
        const { assignReviewers, submissionId, toggleAssignReviewers } = this.props;
        const { checkedReviewers, isBoardMembersOnly } = this.state;

        assignReviewers({
            email,
            reviewers: email ? checkedReviewers : [],
            id: submissionId,
            is_handle: isBoardMembersOnly === 'true' ? 1 : 0,
        }).then(toggleAssignReviewers);
    };

    onSubmit = (email = null) => {
        if (!email) {
            const { openLightbox } = this.props;
            openLightbox(() => this.handleAssignReviewers({}));
        } else {
            this.handleAssignReviewers(email);
        }
    };

    getColumns = () => {
        const { dataAutocomplete, dataInterestsAutocomplete, journalId } = this.props;
        const { checkedReviewers } = this.state;

        return [
            {
                key: 'name',
                col: { width: '45%' },
                title: 'data_table.name',
                handler: item => {
                    const isEditorial = !!item.userRoles.find(userRole => userRole.role && userRole.role.slug === ROLE_EDITORIAL_BOARD && userRole.journal_id === journalId);
                    return (
                        <div className="reviewer-name-cell">
                            { isEditorial ? <span className="editorial-bm"></span> : '' }
                            <span>{ item.name }</span>
                        </div>
                    );
                },
                sortable: true,
            },
            {
                key: 'interests',
                filterKey: 'interests.id',
                col: { width: '45%' },
                title: 'data_table.interests',
                handler: item => {
                    return item.interests.map(interest => interest.title).join(', ');
                },
                filterable: true,
                filterType: DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_MULTI,
                options: dataInterestsAutocomplete.map(item => ({
                    value: item.id,
                    label: item.title,
                })),
                loadOptions: this.loadInterestsOptions,
                relation: 'interests',
                filterPlaceholder: 'data_table.filter.interests',
            },
            {
                key: 'email',
                filterKey: 'id',
                col: { width: '40%' },
                title: 'data_table.email',
                type: 'string',
                options: dataAutocomplete.map(item => ({
                    value: item.id,
                    label: item.email,
                })),
                loadOptions: this.loadOptions,
                filterType: DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
                sortable: true,
                filterable: true,
                filterPlaceholder: 'data_table.filter.email',
            },
            {
                key: 'assign',
                col: { width: '45%' },
                title: 'data_table.assign',
                handler: item => {
                    const assignments = item.assignments
                        .filter(assignment => [
                            ASSIGN_STATUS_ACCEPTED,
                            ASSIGN_STATUS_COMPLETED,
                        ].includes(assignment.status));
                    return !assignments.length ? '' : formatDate(
                        assignments.find(
                            assignment => assignment.id === Math.max.apply(
                                Math,
                                assignments.map(assignment => assignment.id),
                            ),
                        ).created_at,
                    );
                },
            },
            {
                key: 'invitations',
                title: 'data_table.invitations',
                type: DATA_TABLE_COLUMN_TYPE_ICON_STATUS,
                handler: item => {
                    return (
                        <div className="table-icons-wrap">
                            <DoneIcon titleAccess="Agree"/> { item.accepted }
                            <CancelIcon titleAccess="Decline"/> { item.declined }
                        </div>
                    );
                },
            },
            {
                key: 'checked',
                title: 'data_table.assign_action',
                handler: (item) => {
                    return <Checkbox
                        checked={ checkedReviewers.includes(item.id) }
                        onChange={ this.handleChange(item.id) }
                        className="reviewers-assign-checkbox"
                    />;
                },
            },
        ];
    };

    getRadios() {
        const { isBoardMembersOnly } = this.state;
        const { updateFilter } = this.props;

        return (
            <FormControl component="fieldset">
                <RadioGroup
                    row
                    name="isBoardMembersOnly"
                    className="assign-reviewers-radio-group"
                    aria-label="isBoardMembersOnly"
                    value={ isBoardMembersOnly }
                    onChange={ (event, isBoardMembersOnly) =>
                        this.setState({ isBoardMembersOnly }, () => updateFilter(this.getFilter()))
                    }
                >
                    <FormControlLabel
                        value={ 'false' }
                        control={ <Radio/> }
                        label={ <FormattedMessage id={ 'field.assign_type_all.label' }/> }
                    />
                    <FormControlLabel
                        value={ 'true' }
                        control={ <Radio/> }
                        label={ <FormattedMessage id={ 'field.assign_type_board.label' }/> }
                    />
                </RadioGroup>
            </FormControl>
        );
    }

    render() {
        const { data, load, metadata, updateFilter, submissionId, rolesData, toggleAssignReviewers, template, templateHandle } = this.props;
        const { checkedReviewers, isBoardMembersOnly } = this.state;

        if (!rolesData || !rolesData.length) {
            return null;
        }

        return (
            <div className="wrapAssignBlock submissions-list-wrapper">
                <FormGroup title="form_groups.assign_reviewers">
                </FormGroup>

                { this.getRadios() }

                <DataTable
                    columns={ this.getColumns() }
                    metadata={ metadata }
                    data={ data }
                    filter={ this.getFilter() }
                    load={ load }
                    updateFilter={ updateFilter }
                    icon={ false }
                    customCardClass="data-table-icon-card-wrapper"
                />

                <Grid
                    container
                    direction="row"
                    justify="center"
                >
                    <ItemGrid
                        xs={ 12 }
                        sm={ 12 }
                        md={ 6 }
                    >
                        <Button
                            onClick={ toggleAssignReviewers }
                            color="danger"
                        >
                            <FormattedMessage id={ 'button.lightbox.cancel' }/>
                        </Button>
                    </ItemGrid>
                    <ItemGrid
                        xs={ 12 }
                        sm={ 12 }
                        md={ 3 }
                    >
                        <Button
                            onClick={ () => this.onSubmit() }
                            color="info"
                            right
                            customClass="upload-style-btn"
                        >
                            <FormattedMessage id={ 'button.lightbox.skip' }/>
                        </Button>
                    </ItemGrid>
                    <ItemGrid
                        xs={ 12 }
                        sm={ 12 }
                        md={ 3 }
                    >
                        <EmailDialog
                            key={ isBoardMembersOnly }
                            submissionId={ submissionId }
                            template={ isBoardMembersOnly === 'false' ? template : templateHandle }
                            openBtnText={ <FormattedMessage id={ 'button.reviewers.assign' }/> }
                            onSubmit={ this.onSubmit }
                            chooseEmails={ false }
                            openBtnRight
                            skipBtn={ false }
                            openBtnDisabled={ !checkedReviewers.length }
                        />
                    </ItemGrid>
                </Grid>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/SubmissionManagement/containers/ReviewersAssignmentList.jsx